import { Context } from "@/context";
import { useDiceRollPrivacyStore } from "@/hooks/useDiceRollPrivacyStore";
import { useShared3dDiceSettingStore } from "@/hooks/useShared3dDiceSettingStore";
import type { DiceRollEventPayload, DiceSet, RollDicePayload } from "@/types";
import {
  lazy,
  Suspense,
  useContext,
  useEffect,
  type FC,
  type MouseEvent,
} from "react";

export const rollerImport = () =>
  import("./Roller").then(({ Roller }) => ({ default: Roller }));

const Roller = lazy(rollerImport);

export interface CustomDiceRollerProps {
  onClose: () => void;
  rollInstrumentationCallback?: (payload: DiceRollEventPayload) => void;
  rollCallback?: (payload: RollDicePayload) => void;
  rollErrorCallback?: (exception: unknown) => void;
  canUseShared3dDice?: boolean;
  setId?: string;
  clearDiceCallback?: () => void;
  rollToCallback?: (e: MouseEvent<HTMLButtonElement>) => void;
  diceSets: DiceSet[];
  gameId?: number;
  preview?: boolean;
  customDiceSet?: boolean;
  visible?: boolean;
  entityType?: string;
  entityId?: string;
  messageTarget?: number;
  name?: string;
}

export const CustomDiceRoller: FC<CustomDiceRollerProps> = ({
  canUseShared3dDice,
  visible,
  ...rest
}) => {
  const { user } = useContext(Context);
  const { getIsShared3dDiceSettingEnabled, setIsShared3dDiceSettingEnabled } =
    useShared3dDiceSettingStore();
  const { getDiceRollPrivacy, setHasSeenDiceContainer, setRollToPrivacy } =
    useDiceRollPrivacyStore();
  const isUserIdNumber = !isNaN(Number(user?.id));

  useEffect(() => {
    if (
      isUserIdNumber &&
      getIsShared3dDiceSettingEnabled(
        Boolean(canUseShared3dDice),
        Number(user?.id),
      ) === undefined
    ) {
      setIsShared3dDiceSettingEnabled(Number(user?.id));
    }
  }, [
    canUseShared3dDice,
    getIsShared3dDiceSettingEnabled,
    isUserIdNumber,
    setIsShared3dDiceSettingEnabled,
    user,
  ]);

  useEffect(() => {
    const diceContainerConfigJson = JSON.parse(
      localStorage.getItem("diceContainerConfig") || "{}",
    );
    const diceContainerConfigUserSettings =
      diceContainerConfigJson[Number(user?.id)];

    // migrate old dice container privacy settings
    if (diceContainerConfigUserSettings) {
      if (diceContainerConfigUserSettings.hasSeenDiceContainer) {
        setHasSeenDiceContainer(Number(user?.id));
      }

      if (diceContainerConfigUserSettings.rollTo) {
        setRollToPrivacy(
          Number(user?.id),
          diceContainerConfigUserSettings.rollTo,
        );
      }

      // remove old settings after migration
      delete diceContainerConfigJson[Number(user?.id)];

      const userConfigsCount = Object.keys(diceContainerConfigJson).length;

      if (userConfigsCount) {
        localStorage.setItem(
          "diceContainerConfig",
          JSON.stringify(diceContainerConfigJson),
        );
      } else {
        localStorage.removeItem("diceContainerConfig");
      }
    }

    if (isUserIdNumber && !getDiceRollPrivacy(Number(user?.id))) {
      setRollToPrivacy(Number(user?.id));
    }
  }, [
    getDiceRollPrivacy,
    isUserIdNumber,
    setHasSeenDiceContainer,
    setRollToPrivacy,
    user,
  ]);

  if (!visible) return null;

  return (
    <Suspense>
      <Roller canUseShared3dDice={canUseShared3dDice} {...rest} />
    </Suspense>
  );
};
